import React, { useState, useContext, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import currencyFormatter from 'src/utils/currencyFormatter';
import formatAddress from 'src/utils/addressFormatter';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faCheck } from '@fortawesome/free-solid-svg-icons';
import { HashLink } from 'react-router-hash-link';
import { DateTime } from 'luxon';
import { preBidSale, enterableSale } from 'src/utils/upcomingSale';
import TireDisplay from './TireDisplay';
import BrakeDisplay from './BrakeDisplay';
import ConditionDisplay from './ConditionDisplay';
import PageHeader from '../Headers/PageHeader';
import { Loading, Error } from 'src/components/DataState';
import { numberWithDelimiter as delimited } from 'src/utils/stringHelpers';
import PreBid from './PreBid';
import BidStatusBanner from './BidStatusBanner';
import VideoPlayer from './VideoPlayer';
import Watch from './Watch';
import Navigator from './Navigator';
import AuthContext from 'src/contexts/AuthContext';
import FinanceLink from '../FinanceLink';

import { ASSET } from './queries';
import {
  AssetContainer,
  AssetContent,
  AssetDetails,
  AssetImage,
  AssetTop,
  AssetBottom,
  AssetAuctionDate,
  AssetBuyNowPrice,
  AssetButtonContainer,
  AssetLinkContainer,
  AssetLink,
  AssetSidebarDetails,
  AssetDetailRow,
  AssetDetailCloseRow,
  AssetDetailLabel,
  AssetDetail,
  AssetSpecifications,
  TireSection,
  BrakeSection
} from './elements';
import {
  NarrowButton,
  BoldTitle,
  Label,
  SectionHeader,
  SectionSubHeader,
  Section,
  RightColumn,
  LeftColumn,
  SectionLabel,
  SectionDetail,
  SectionDetails,
  FeaturesSection,
  Feature,
  UnevenSectionDetails
} from '../../elements';
import { elGray, elLightGray, elRed, iconGray } from 'src/styles/colors';
import { Link } from 'react-router-dom';
import AssetPhoto from 'src/components/shared/AssetImage';
import ImageLightbox from 'src/components/shared/ImageLightbox';
import { photoSrc, isVideo } from 'src/utils/photoSrc';
import { useBrokenMedia } from 'src/hooks/useBrokenMedia';
import useWindowWidth from 'src/hooks/useWindowWidth';
import useSaleRegistration from 'src/hooks/useSaleRegistration';
import { TABLET_MIN_WIDTH } from 'src/styles/breakpoints';

const Carousel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 100%;
  margin-top: 10px;
  gap: 8px;
  padding: 4px 0;
  overflow-x: auto;
  overflow-y: hidden;

  @media(max-width: 768px) {
    gap: 5px;
    max-width: 90%;
  }
`;

const MainPhoto = styled.div`
  align-items: center;
  text-align: center;
  cursor: zoom-in;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const Thumb = styled.div`
  flex: 0 0 auto;
  width: ${({ $smallScreen }) => $smallScreen ? '60px' : '90px'};
  cursor: pointer;
  border-radius: 5px;
  outline: 3px solid ${props => props.selected ? elRed : 'transparent'};
`;

const VideoThumb = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 75%;
  background: #000;
  border-radius: 5px;

  & > span {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: bold;
    font-size: 12px;
  }
`;

const AssetName = styled.div`
  font-size: 20px;
  font-weight: 600;

  @media(min-width: 768px) {
    font-size: 50px;
    line-height: 69px;
  }
`;

const NavigatorContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
`;

const Asset = ({props}) => {
  const windowWidth = useWindowWidth();
  const smallScreen = windowWidth < TABLET_MIN_WIDTH;
  const { isLoggedIn } = useContext(AuthContext);
  const { id, soarrId, buyNow, saleId } = useParams();

  const [fetchedAt, setFetchedAt] = useState(null);

  const { data, error, refetch } = useQuery(ASSET, {
    variables: { id, soarrId },
    // Stamps every completed fetch, including refetches after a bid, so the
    // standing banner can say how current it is.
    onCompleted: () => setFetchedAt(new Date()),
  });
  let currentSaleId = saleId;

  const [mediaIndex, setMediaIndex] = useState(0);
  const [engineSpecs, setEngineSpecs] = useState([]);
  const [suspensionSpecs, setSuspensionSpecs] = useState([]);
  const [drivetrainSpecs, setDrivetrainSpecs] = useState([]);
  const [wheelsSpecs, setWheelsSpecs] = useState([]);
  const [sleeperSpecs, setSleeperSpecs] = useState([]);
  const [boxSpecs, setBoxSpecs] = useState([]);
  const [otherSpecs, setOtherSpecs] = useState([]);
  const [showPreBid, setShowPreBid] = useState(false);
  const [showWatch, setShowWatch] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { broken, markBroken, skipBroken } = useBrokenMedia(mediaIndex, setMediaIndex);
  const galleryRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { register, registered } = useSaleRegistration(
    data?.asset?.activeSaleAsset?.sale?.id
  );

  useEffect(() => {
    setEngineSpecs([]);
    setSuspensionSpecs([]);
    setDrivetrainSpecs([]);
    setWheelsSpecs([]);
    setSleeperSpecs([]);
    setBoxSpecs([]);
    setOtherSpecs([]);
  }, [location])

  // Without this the page spins forever on a failed query, since `data` never
  // arrives to clear the loading state. Guarded on `!data` so a failed refetch
  // leaves the loaded asset on screen rather than replacing it.
  if (error && !data) { return <Error error={error} />; }
  if (!data) { return <Loading />; }
  if (!data.asset) {
    navigate('/inventory');
    return <Loading />;
  }

  const { asset } = {...data};
  const activeSaleAsset = asset.activeSaleAsset;
  const roomOpen = enterableSale(activeSaleAsset?.sale);
  let allMedia = [...asset?.photos, ...(asset?.soarrPhotos || [])];

  // Don't display past sale info
  let saleOn = activeSaleAsset?.sale?.saleOn ?
    DateTime.fromISO(activeSaleAsset.sale.saleOn) : null;

  if (saleOn && saleOn < DateTime.now().minus({ days: 2 })) {
    saleOn = null;
  }

  const goToNextMedia = () => {
    if (mediaIndex >= allMedia.length - 1) {
      setMediaIndex(0);
    } else {
      setMediaIndex(mediaIndex + 1);
    }
  };

  if(asset?.soarrVideoUrls?.length > 0) {
    allMedia = [...allMedia, ...asset?.soarrVideoUrls]
  }

  const media = allMedia[mediaIndex];

  // Photos lead allMedia, so mediaIndex < photoCount means the current item is a photo.
  const photoCount =
    (asset.photos?.length || 0) + (asset.soarrPhotos?.length || 0);
  const photoSrcs = allMedia.slice(0, photoCount).map(m => photoSrc(m, 'detail'));

  const goToPrevMedia = () => {
    setMediaIndex(mediaIndex <= 0 ? allMedia.length - 1 : mediaIndex - 1);
  };

  const focusGallery = () => galleryRef.current?.focus();

  const openLightbox = () => {
    if (mediaIndex < photoCount) setLightboxOpen(true);
  };

  const handleGalleryKey = event => {
    if (lightboxOpen) return;
    if (event.key === 'ArrowRight') { event.preventDefault(); goToNextMedia(); }
    else if (event.key === 'ArrowLeft') { event.preventDefault(); goToPrevMedia(); }
  };

  const generalDetails = {...asset.generalDetails}
  const details = {...asset.generalDetails?.unit};
  const tires = asset.tireDetails;
  const brakes = asset.brakeDetails;
  const condition = asset.conditionDetails;

  const videoOrPhoto = () => {
    if(isVideo(media)) {
      return <VideoPlayer video={media} />
    } else {
      return (
        <MainPhoto onClick={() => { focusGallery(); openLightbox(); }}>
          <AssetPhoto
            src={broken.has(mediaIndex) ? null : photoSrc(media, 'detail')}
            alt={asset?.name}
            onError={() => skipBroken(allMedia.length)}
          />
        </MainPhoto>
      )
    }
  }

  return (
    <>
      <PageHeader title="Inventory Detail" />
      <AssetContainer>
        <NavigatorContainer>
          <Navigator
            saleId={currentSaleId}
          />
        </NavigatorContainer>
        <FinanceLink containerMargin='10px' />
        <AssetContent>
          <AssetTop>
            <AssetImage
              ref={galleryRef}
              tabIndex={0}
              onKeyDown={handleGalleryKey}
              style={{ outline: 'none' }}
            >
              {videoOrPhoto()}
              <Carousel>
                {allMedia?.map((media, index) => (
                  broken.has(index) ? null :
                  <Thumb
                    key={index}
                    $smallScreen={smallScreen}
                    selected={mediaIndex === index}
                    onClick={() => { setMediaIndex(index); focusGallery(); }}
                  >
                    {isVideo(media) ?
                      <VideoThumb><span>Video</span></VideoThumb> :
                      <AssetPhoto
                        src={photoSrc(media, 'thumbnail')}
                        alt={asset?.name}
                        onError={() => markBroken(index)}
                      />
                    }
                  </Thumb>
                ))}
              </Carousel>
            </AssetImage>
            <ImageLightbox
              open={lightboxOpen}
              images={photoSrcs}
              index={mediaIndex}
              alt={asset?.name}
              onIndexChange={setMediaIndex}
              onClose={() => setLightboxOpen(false)}
              onImageError={() => skipBroken(photoCount)}
            />
            <AssetDetails>
              <AssetName>{asset?.name}</AssetName>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between'
                }}
              >
              <AssetAuctionDate>
                <span
                  style={{
                    color: 'red',
                    marginLeft: smallScreen ? '0px' : '5px',
                    fontSize: smallScreen ? '18px' : '30px',
                    marginTop: smallScreen ? '0px' : '-4px',
                    fontWeight: 400
                  }}
                >
                  {getSaleInfo()}
                </span>
                </AssetAuctionDate>
                {buyNow === 'true' ?
                  <AssetBuyNowPrice>
                    <span
                      style={{
                        color: 'red',
                        fontSize: smallScreen ? '18px' : '30px',
                        marginTop: smallScreen ? '0px' : '-4px'
                      }}
                    >
                      {`BUY NOW PRICE: ${
                        currencyFormatter(parseFloat(asset.listPrice))
                      }`}
                    </span>
                  </AssetBuyNowPrice> :
                  null
                }
              </div>
              <AssetButtonContainer>
                <NarrowButton
                  to={buyNow ? `/contact/assetId=${asset.id}` : '/contact'}
                  contact={'true'}
                >
                  Contact Us
                </NarrowButton>
                {preBidSale(activeSaleAsset?.sale) && !roomOpen && (
                  <NarrowButton
                    to={isLoggedIn ?
                      `/bidding/${activeSaleAsset.sale.id}` : "/login"
                    }
                    target={isLoggedIn ? "_self" : "_blank"}
                    onClick={e => {
                      if(isLoggedIn) {
                        e.preventDefault();
                        e.stopPropagation();

                        register();
                        setShowPreBid(true);
                      }
                    }}
                  >
                    {`${!isLoggedIn || !registered ?
                      'Click to Register to ' : ''} Enter Pre-Bid`
                    }
                  </NarrowButton>
                )}
                {roomOpen && (
                  <NarrowButton
                    to={isLoggedIn ? `/bidding/${activeSaleAsset.sale.id}` : "/login"}
                    target={isLoggedIn ? "_self" : "_blank"}
                  >
                    {`${isLoggedIn ? '' : 'Click to Register to '} Enter Auction`}
                  </NarrowButton>
                )}
                {buyNow &&
                  <NarrowButton
                    to={`/contact/assetId=${asset.id}`}
                  >
                    Buy Now
                  </NarrowButton>
                }
                <NarrowButton
                  to={isLoggedIn ? "/inventory" : "/login"}
                  target="_blank"
                  style={asset.buyerUserAssetView?.watching ?
                    { backgroundColor: 'green', color: 'white' } :
                    {}
                  }
                  onClick={e => {
                    if(isLoggedIn) {
                      e.preventDefault();
                      e.stopPropagation();

                      register();
                      setShowWatch(true);
                    }
                  }}
                >
                  {`${!isLoggedIn || !registered ?
                    'Click to Register to ' : ''}${asset.buyerUserAssetView?.watching ?
                      'Watching' : 'Watch'
                    }`
                  }

                </NarrowButton>
              </AssetButtonContainer>
              <BidStatusBanner
                preBidData={asset.preBidData}
                fetchedAt={fetchedAt}
              />
              {showPreBid && preBidSale(asset.activeSaleAsset?.sale) && (
                <PreBid
                  asset={asset}
                  setShow={setShowPreBid}
                  refetch={refetch}
                />
              )}
              {showWatch && (
                <Watch
                  asset={asset}
                  setShow={setShowWatch}
                  refetch={refetch}
                />
              )}
              <AssetLinkContainer>
                <BoldTitle>OVERVIEW</BoldTitle>
              </AssetLinkContainer>
              <AssetSidebarDetails>
                <AssetDetailRow>
                  <AssetDetailLabel>
                    {details ?
                      details.segment?.name || 'Type' :
                      `${asset.segment ? asset.segment : asset.category + ' '}Type`
                    }
                  </AssetDetailLabel>
                  <AssetDetail>
                    {asset.subCategory}
                  </AssetDetail>
                </AssetDetailRow>
                <AssetDetailRow>
                  <AssetDetailLabel>Stock#</AssetDetailLabel>
                  <AssetDetail>
                    {details ?
                      details['stockNumber'] :
                      asset.stockNumber
                    }
                  </AssetDetail>
                </AssetDetailRow>
                {asset.miles !== 0 && asset.miles && (
                  <AssetDetailRow>
                    <AssetDetailLabel>Odometer</AssetDetailLabel>
                    <AssetDetail>
                      {asset.miles ? `${delimited(asset.miles)} mi` : ''}
                    </AssetDetail>
                  </AssetDetailRow>
                )}
                {details && details['odometer'] && details['odometer']['ecm'] &&
                  Object.keys(details['odometer']['ecm']).length ?
                  <AssetDetailRow>
                    <AssetDetailLabel>ECM Reading</AssetDetailLabel>
                    <AssetDetail>
                      {details && details['odometer']['ecm']}
                    </AssetDetail>
                  </AssetDetailRow> :
                  null
                }
                {asset.vin ?
                  <AssetDetailRow>
                    <AssetDetailLabel>VIN</AssetDetailLabel>
                    <AssetDetail>
                      {asset.vin}
                    </AssetDetail>
                  </AssetDetailRow> :
                  null
                }
                {asset?.inspectionUrl ?
                  <AssetDetailRow>
                    <AssetDetailLabel>
                      Inspection:
                    </AssetDetailLabel>
                    <AssetDetail>
                      <a
                        href={asset?.inspectionUrl}
                        target="_blank"
                        rel="nofollow"
                      >
                        {asset?.inspectionUrl}
                      </a>
                    </AssetDetail>
                  </AssetDetailRow> :
                  null
                }
                {asset?.nisInspectionUrl ?
                  <AssetDetailRow>
                    <AssetDetailLabel>
                      Additional Inspections:
                    </AssetDetailLabel>
                    <AssetDetail>
                      <a
                        href={asset?.nisInspectionUrl}
                        target="_blank"
                        rel="nofollow"
                      >
                        View Additional Inspection
                      </a>
                    </AssetDetail>
                  </AssetDetailRow> :
                  null
                }
              </AssetSidebarDetails>
              <AssetLinkContainer>
                <BoldTitle>LOCATION DETAILS</BoldTitle>
                {/* <AssetLink
                  onClick={() => {}}
                >
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    size='sm'
                    style={{color: iconGray, marginLeft: '5px'}}
                    onClick={() => {}}
                  />
                  Directions
                </AssetLink> */}
              </AssetLinkContainer>
              <AssetSidebarDetails>
                {(details || asset.location) &&
                  <>
                    <AssetDetailLabel>
                      {details && details['dealer'] ?
                        details['dealer']['name'] :
                        asset.location?.name
                      }
                    </AssetDetailLabel>
                    {
                      details ?
                        `This unit is located in ${details['physicalLocation']}` :
                        `This unit is located in ${asset.location?.city}, ${asset.location?.state}`
                    }
                    {asset.location?.phoneNumber || (details && details['dealer'] && details['dealer']['phone']) ?
                      <AssetDetailCloseRow style={{marginTop: '20px'}}>
                        Phone:
                        <span style={{marginLeft: '5px'}}>
                          {details && details['dealer'] ?
                            details['dealer']['phone'] :
                            asset.location?.phoneNumber?.formatted
                          }
                        </span>
                      </AssetDetailCloseRow> :
                      null
                    }
                    {asset.location ?
                      <AssetDetailCloseRow style={{marginTop: '5px'}}>
                        Directions:
                        <Link
                          to={`/locations`}
                          style={{textDecoration: 'none', cursor: 'pointer',
                            color: '#000', marginLeft: '5px'
                          }}
                        >
                          View Locations
                        </Link>
                      </AssetDetailCloseRow> :
                      null
                    }
                  </>
                }
                {(!details && !asset.location) &&
                  (asset.city && asset.state) &&
                  <AssetDetailRow>
                    {`This unit is located in ${[asset.city, asset.state].filter(Boolean).join(', ')}`}
                  </AssetDetailRow>
                }
              </AssetSidebarDetails>
            </AssetDetails>
          </AssetTop>
          <AssetBottom>
            <AssetSpecifications id="specs">
              {details && details['attributes'] && Object.keys(details['attributes']).length ?
                <>
                  <SectionHeader>
                    Specifications
                  </SectionHeader>
                  {checkInObject('Engine') &&
                    getSpecs('Engine')
                  }
                  {checkInObject('Suspension') &&
                    getSpecs('Suspension')
                  }
                  {checkInObject('Drivetrain') &&
                    getSpecs('Drivetrain')
                  }
                  {checkInObject('Wheels & Tires') &&
                    getSpecs('Wheels & Tires')
                  }
                  {checkInObject('Sleeper') &&
                    getSpecs('Sleeper')
                  }
                  {checkInObject('Box') &&
                    getSpecs('Box')
                  }
                  {checkInObject('Other') &&
                    getSpecs('Other')
                  }
                </> :
                null
              }
            </AssetSpecifications>
            <div
              style={{
                display: 'flex',
                flexDirection: smallScreen ? 'column' : 'row',
                flex: 1
              }}
            >
              <LeftColumn
                style={{minWidth: '50%', paddingLeft: '2%'}}
              >
                <SectionHeader>
                  Additional Features
                </SectionHeader>
                <FeaturesSection>
                  {generalDetails && generalDetails['unit'] && Object.keys(generalDetails['unit']['options']).length ?
                    generalDetails['unit']['options'].map(option => {
                      return (
                        <Feature key={option}>
                          <FontAwesomeIcon
                            icon={faCheck}
                            size='sm'
                            style={{color: elRed, marginLeft: '5px',
                              marginRight:'10px'
                            }}
                            onClick={() => {}}
                          />
                          {option}
                        </Feature>
                      )
                    }) :
                    null
                  }
                </FeaturesSection>
              </LeftColumn>
              <RightColumn
                style={{
                  borderLeft: smallScreen ? 'none' : '1px solid black',
                  paddingLeft: '2%'
                }}
              >
                <SectionHeader>TIRES</SectionHeader>
                <TireSection>
                  <TireDisplay tires={tires} />
                </TireSection>
              </RightColumn>
            </div>
            <UnevenSectionDetails>
              <LeftColumn
                style={{lineHeight: '44px', backgroundColor: elLightGray,
                  marginTop: '50px', paddingLeft: '2%'
                }}
              >
                {asset.notes &&
                  <>
                    <SectionHeader>Additional Description</SectionHeader>
                    <Section
                      style={{ whiteSpace: 'pre-line', lineHeight: '32px' }}
                    >
                      {asset.notes}
                    </Section>
                  </>
                }
                {/* Video Section */}
                <Section>

                </Section>
              </LeftColumn>
              <RightColumn
                style={{lineHeight: '44px', backgroundColor: elLightGray,
                  marginTop: '50px', paddingLeft: '2%'
                }}
              >
                <SectionHeader>Brakes</SectionHeader>
                <BrakeSection>
                  <div className="brakes grid-x small-up-2">
                    <BrakeDisplay brakes={brakes} />
                  </div>
                </BrakeSection>
              </RightColumn>
            </UnevenSectionDetails>
            <SectionHeader>CONDITION INFORMATION</SectionHeader>
            <ConditionDisplay condition={condition} />
          </AssetBottom>
        </AssetContent>
      </AssetContainer>
    </>
  );

  function getSaleInfo() {
    if(saleOn) {
      const saleInfo = `SALE DATE ${saleOn.toFormat('D')}`;
      if (asset.correctedLotNumber) {
        return `${saleInfo}  Run # ${asset.correctedLotNumber}`;
      }
    }else {
      return 'SALE DATE TBA'
    }
  }

  function checkInObject(attribute) {
    if(!details || !details['attributes']) return false;
    let inObject = false;
    let keys = Object.keys(details['attributes']);
    switch(attribute) {
      case 'Engine':
        inObject = keys.includes('Engine Make' || 'Engine Model' ||
          'Engine Serial' || 'Engine HP' || 'Fuel Type' || 'Engine Brake' ||
          'Tank #1 Capacity' || 'Tank #2 Capacity'
        )
        if(inObject && !engineSpecs.length) {
          setSpecs(
            attribute,
            ['Engine Make', 'Engine Model', 'Engine Serial', 'Engine HP',
              'Fuel Type', 'Engine Brake','Tank #1 Capacity', 'Tank #2 Capacity']
          )
        }
        break;
      case 'Suspension':
        inObject = keys.includes('Suspension Type' || 'FA Capacity' ||
          'RA Capacity' || 'Suspension Make'
        )
        if(inObject && !suspensionSpecs.length) {
          setSpecs(
            attribute,
            ['Suspension Type', 'FA Capacity', 'RA Capacity', 'Suspension Make']
          )
        }
        break;
      case 'Drivetrain':
        inObject = keys.includes('Trans Make' || 'Trans Model' ||
          'Trans Speed' || 'Axle Configuration' || 'Rear End Ratio' ||
          'Wheelbase'
        )
        if(inObject && !drivetrainSpecs.length) {
          setSpecs(
            attribute,
            ['Trans Make', 'Trans Model', 'Trans Speed', 'Axle Configuration',
              'Rear End Ratio', 'Wheelbase']
          )
        }
        break;
      case 'Wheels & Tires':
        inObject = keys.includes('Front Tire Size' || 'Rear Tire Size' ||
          'Front Wheels' || 'Rear Wheels' || 'Brakes'
        )
        if(inObject && !wheelsSpecs.length) {
          setSpecs(
            attribute,
            ['Front Tire Size', 'Rear Tire Size', 'Front Wheels', 'Rear Wheels',
              'Brakes']
          )
        }
        break;
      case 'Box':
        inObject = keys.includes('Box Make' || 'Box Length' || 'Box Width' ||
          'Box Height' || 'Lift Gate' || 'Door Opening' || 'Floor Material')
        if(inObject && !boxSpecs.length) {
          setSpecs(
            attribute,
            ['Box Make', 'Box Length', 'Box Width', 'Box Height', 'Lift Gate',
              'Door Opening', 'Floor Material']
          )
        }
        break;
      case 'Sleeper':
        inObject = keys.includes('Sleeper Type' || 'Sleeper Size');
        if(inObject && !sleeperSpecs.length) {
          setSpecs(
            attribute,
            ['Sleeper Type', 'Sleeper Size']
          )
        }
        break;
      case 'Other':
        inObject = keys.includes('5th Wheel' || 'GVWR' || 'Fairings' ||
          'Frame Material' || 'Frame Type' || 'Color' || 'Seats' ||
          'Interior' || 'Interior Color' || 'Trans Type' || 'Crane Make' ||
          'Lift Gate Capacity' || 'Reefer Make' || 'Reefer Model' ||
          'Reefer Hours' || 'Reefer Serial' || '# Axles' || 'APU' || 'Length' ||
          'Width' || 'Paint Code' || 'Body Make' || 'Body Model' ||
          'Body Material' || 'Under CDL' || 'Engine Year' || 'Rear Doors'
        )
        if(inObject && !otherSpecs.length) {
          setSpecs(
            attribute,
            ['5th Wheel', 'GVWR', 'Fairings', 'Frame Material', 'Frame Type',
              'Color', 'Seats', 'Interior', 'Interior Color', 'Trans Type',
              'Crane Make', 'Lift Gate Capacity', 'Reefer Make', 'Reefer Model',
              'Reefer Hours', 'Reefer Serial', '# Axles', 'APU', 'Length',
              'Width', 'Paint Code', 'Body Make', 'Body Model', 'Body Material',
              'Under CDL', 'Engine Year', 'Rear Doors']
          )
        }
        break;
      default:
        break;
    }
    return inObject;
  }

  function setSpecs(attribute, specArray) {
    let specs = [];
    specArray.map(key => {
      if(key in details['attributes']) {
        specs.push({key: key, value: details['attributes'][key]})
      }
    });
    switch(attribute) {
      case 'Engine':
        setEngineSpecs(specs);
        break;
      case 'Suspension':
        setSuspensionSpecs(specs);
        break;
      case 'Drivetrain':
        setDrivetrainSpecs(specs);
        break;
      case 'Wheels & Tires':
        setWheelsSpecs(specs);
        break;
      case 'Box':
        setBoxSpecs(specs);
        break;
      case 'Sleeper':
        setSleeperSpecs(specs);
        break;
      case 'Other':
        setOtherSpecs(specs);
        break;
      default:
        break;
    }
  }

  function getSpecs(attribute) {
    let specs;
    switch(attribute) {
      case 'Engine':
        specs = engineSpecs;
        break;
      case 'Suspension':
        specs = suspensionSpecs;
        break;
      case 'Drivetrain':
        specs = drivetrainSpecs;
        break;
      case 'Wheels & Tires':
        specs = wheelsSpecs;
        break;
      case 'Box':
        specs = boxSpecs;
        break;
      case 'Sleeper':
        specs = sleeperSpecs;
        break;
      case 'Other':
        specs = otherSpecs;
        break;
      default:
        break;
    }

    return (
      <>
        <SectionSubHeader>
          {attribute}
        </SectionSubHeader>
        <Section>
          {specs.map(spec => {
            return (
              <SectionDetails key={spec.key}>
                <SectionLabel>{spec.key}</SectionLabel>
                <SectionDetail>
                  {spec.value}
                </SectionDetail>
              </SectionDetails>
            )
          })}
        </Section>
      </>
    )
  }
};

export default Asset;
